import { useEffect, useState } from "react";

import type { ResultsCookie } from "types/cookie";

import useAppContext from "../../AppContext";
import { loadResultsCookieByBaseUrl } from "../storeResultsData";
import useUrlSlugContext from "components/UrlSlugContext";
import { pageView } from "utils/analytics";
import ContentGutter from "components/ContentGutter";
import SiteWrapper from "components/SiteWrapper";
import AccountCreateModal from "../AccountCreateModal";
import ClinicalEligible from "../ResultsClinical/ClinicalEligible";
import ClinicalIneligible from "../ResultsClinical/ClinicalIneligible";
import ClinicalIneligibleSuicide from "../ResultsClinical/ClinicalIneligibleSuicide";
import clearStorageAndCookies from "utils/clearStorageAndCookies";
import LoadingInterstitial from "../LoadingInterstitial";
import { useFetchAssessmentClinicalEligibility } from "utils/assessment";
import { FINISHED } from "utils/resultFunctions/constants";
import { SECONDS_IN_THIRTY_DAYS } from "utils/cookies";
import { WL_REASON_ELI_SUICIDE } from "utils/eligibility";

interface AssessmentClinicalEligibilityInfo {
  assessmentId: string;
  info: {
    wwBrowserId?: string;
    startTime?: string;
    endTime?: string;
    updatedAt?: string;
    createdAt?: string;
    slugs?: string;
    result?: string;
    resultsScreen?: string;
    eligibleSequence?: boolean;
  };
}

const ResultsRestoredClinical = (): JSX.Element => {
  const [clinicalEligible, setClinicalEligible] = useState(false);
  const [clinicalResultsScreen, setClinicalResultsScreen] = useState<string>();
  const { profile, setProfile, setAuthData, authData } = useAppContext();
  const { loggedInAs } = authData || {};
  const { baseUrl } = useUrlSlugContext();
  const cookie = loadResultsCookieByBaseUrl(baseUrl) as ResultsCookie | null;
  const { getAssessmentClinicalEligibility, info, isSubmitting } =
    useFetchAssessmentClinicalEligibility();

  useEffect(() => {
    pageView("result-revisited");
  }, []);

  useEffect(() => {
    if (!cookie?.profile || !setProfile || !setAuthData || !loggedInAs) {
      return;
    }

    if (profile?.seqAssessmentId === cookie?.aid) {
      setProfile({
        ...profile,
        ...cookie?.profile
      });

      getAssessmentClinicalEligibility({
        assessmentId: profile.seqAssessmentId
      });
    } else {
      clearStorageAndCookies();
      setAuthData({
        ...authData,
        loggedInAs: ""
      });
    }
  }, [setProfile, setAuthData, loggedInAs]);

  useEffect(() => {
    if (isSubmitting || !info || !setAuthData) {
      return;
    }

    // Check logged in user's profile.seqAssessmentId.
    const { info: eligibilityInfo } = info as AssessmentClinicalEligibilityInfo || {};

    // If the user's profile.seqAssessmentId is not finished,
    // send them back to the beginning of the quiz.
    if (!eligibilityInfo?.endTime || eligibilityInfo?.result !== FINISHED || typeof eligibilityInfo?.eligibleSequence !== "boolean") {
      clearStorageAndCookies();
      setAuthData({
        ...authData,
        loggedInAs: ""
      });

      return;
    }

    const thirtyDaysAgo = new Date(
      Date.now() - SECONDS_IN_THIRTY_DAYS * 1000
    );
    const assessmentDate = new Date(eligibilityInfo.endTime);

    if (assessmentDate > thirtyDaysAgo) {
      // If profile.seqAssessmentId is less than thirty days old,
      // use the eligibleSequence result from this assessment.
      setClinicalEligible(eligibilityInfo.eligibleSequence);
      setClinicalResultsScreen(eligibilityInfo.resultsScreen);
    } else {
      // If profile.seqAssessmentId is more than thirty days old,
      // send them back to the beginning of the quiz.
      clearStorageAndCookies();
      setAuthData({
        ...authData,
        loggedInAs: ""
      });
    }
  }, [isSubmitting, info]);

  return (
    <>
      <LoadingInterstitial />
      <ContentGutter>
        <SiteWrapper size="small">
          <AccountCreateModal />
          {profile?.seqAssessmentId && clinicalEligible && <ClinicalEligible />}
          {profile?.seqAssessmentId && !clinicalEligible && clinicalResultsScreen === WL_REASON_ELI_SUICIDE && <ClinicalIneligibleSuicide />}
          {profile?.seqAssessmentId && !clinicalEligible && clinicalResultsScreen !== WL_REASON_ELI_SUICIDE && (
            <ClinicalIneligible />
          )}
        </SiteWrapper>
      </ContentGutter>
    </>
  );
};

export default ResultsRestoredClinical;
